import { create } from 'zustand';
import {
    DEFAULT_TOOL_SETTINGS,
    DOCUMENT_WIDE_ROLES,
    HISTORY_LIMIT,
    TOOLS,
    ZOOM_MAX,
    ZOOM_MIN,
    ZOOM_STEPS,
} from '../constants';
import { cloneObject, newId } from '../engine/objects';
import { layoutNotes } from '../engine/footnotes';
import { measuredTextHeight, TEXT_PAD, recomputeTable } from '../engine/drawObjects';
import { OBJ } from '../constants';
import { markRange, runStyleOf, runsOf, shiftMarks } from '../engine/richText';
import { blockText } from '../engine/docHtml';

/**
 * Editor state. Pages and annotations are held immutably so undo/redo can be a
 * plain snapshot stack — the document is small enough (metadata + objects, not
 * page rasters) that copying it per edit is free.
 */

const emptyDoc = {
    fileName: '',
    sourceBytes: null,
    pdf: null,
    pages: [],
    objectsByUid: {},
    bookmarks: [],
    revisions: [],
};

/** What review markup an object carries, taken back off once it is resolved. */
const stripReviewMarks = (obj) => {
    const { pendingDelete, reviewMark, ...rest } = obj;
    return rest;
};

const newRevision = (kind, uid, objectId, before = null) => ({
    id: newId('rev'),
    kind,
    uid,
    objectId,
    before,
    at: Date.now(),
});


/**
 * Carry an edit of a document-wide stamp on to its twin on every other page.
 *
 * Geometry is carried as a FRACTION of the page, not as points: a document can
 * hold pages of different sizes (a resize applied to one page, an A3 sheet
 * inserted into an A4 file), and copying `x: 300` on to a narrower page puts
 * the watermark off the edge of it. A page that is the same size as the one
 * being edited gets the same numbers back, to the pixel.
 *
 * @param {object} state the store state being rebuilt
 * @param {string} uid the page the edit happened on
 * @param {object} target the object as it was BEFORE the patch
 * @param {object} patch what changed
 */
/**
 * Document-mode re-flow + pagination. Treats the non-role text objects as one top-to-
 * bottom flow (reading order = page order, then y), keeps the spacing already between
 * them, and re-stacks the flow down the pages — starting a new page whenever the next box
 * would cross the bottom margin, and dropping pages that end up empty. Role objects
 * (header/footer/watermark/page numbers) stay on their page. Pure: returns a new
 * { pages, objectsByUid }; page geometry + margins come from the current pages/content.
 */
function paginateDocument(pages, prevByUid, nextByUid, margins = null) {
    if (!pages?.length) return { pages, objectsByUid: nextByUid };
    const pageW = pages[0].width;
    const pageH = pages[0].height;

    // Reading order + the spacing between boxes are read from the PRE-edit layout
    // (prevByUid) — it is internally consistent, whereas nextByUid has only the edited
    // box resized, so its gaps would be wrong. Placement then uses the NEW heights.
    const roleIdsByUid = {};
    const order = [];               // { id, src } in reading order
    const prevPos = {};             // id → { y, h, src }
    for (const pg of pages) {
        const roles = [];
        const flows = [];
        for (const o of prevByUid[pg.uid] || []) (o.role ? roles : flows).push(o);
        if (roles.length) roleIdsByUid[pg.uid] = roles.map((r) => r.id);
        flows.sort((a, b) => a.y - b.y);
        for (const o of flows) { order.push({ id: o.id, src: pg.uid }); prevPos[o.id] = { x: o.x, y: o.y, w: o.w, h: o.h, src: pg.uid }; }
    }
    if (!order.length) return { pages, objectsByUid: nextByUid };

    // Margins given by the document (manual documents have unequal ones, e.g. room for a stamp);
    // otherwise the first box's y is the top margin, mirrored as the bottom one.
    const topY = margins?.top != null ? margins.top : Math.max(0, prevPos[order[0].id].y);
    const bottomLimit = pageH - (margins?.bottom != null ? margins.bottom : topY);
    const DEFAULT_GAP = 8;                            // spacing across a page break

    // Rows: boxes that sit SIDE BY SIDE (level with each other, not overlapping
    // across) move together — a heading with its marks pushed to the right edge,
    // two signature columns. Stacked one under another they would come apart.
    const rows = [];
    for (const it of order) {
        const p = prevPos[it.id];
        const row = rows[rows.length - 1];
        const beside = row
            && row.src === p.src
            && p.y < row.bottom - 0.5
            && row.items.every((r) => {
                const q = prevPos[r.id];
                return p.x >= q.x + q.w - 1 || p.x + p.w <= q.x + 1;
            });
        if (beside) {
            row.items.push(it);
            row.bottom = Math.max(row.bottom, p.y + p.h);
        } else {
            rows.push({ src: p.src, top: p.y, bottom: p.y + p.h, items: [it] });
        }
    }
    const gapAfter = rows.map((row, i) => {
        const next = rows[i + 1];
        if (!next) return 0;
        return row.src === next.src ? Math.max(0, next.top - row.bottom) : DEFAULT_GAP;
    });

    // The edited objects, by id, to place with their new heights.
    const nextById = {};
    for (const pg of pages) for (const o of nextByUid[pg.uid] || []) nextById[o.id] = o;

    const placed = [];
    let pageIdx = 0;
    let y = topY;
    rows.forEach((row, i) => {
        const members = row.items
            .map((it) => ({ o: nextById[it.id], offset: prevPos[it.id].y - row.top }))
            .filter((m) => m.o);
        if (!members.length) return;
        const h = Math.max(...members.map((m) => m.offset + (m.o.h || 0)));
        if (y + h > bottomLimit && placed.some((p) => p.pageIdx === pageIdx)) {
            pageIdx += 1;
            y = topY;
        }
        for (const m of members) placed.push({ o: m.o, pageIdx, y: y + m.offset });
        y += h + gapAfter[i];
    });

    const needed = pageIdx + 1;
    const newPages = [];
    for (let i = 0; i < needed; i += 1) {
        newPages.push(i < pages.length
            ? pages[i]
            : { uid: newId('pg'), sourceIndex: null, width: pageW, height: pageH, baseRotation: 0, userRotation: 0 });
    }

    const newObjs = {};
    for (const pg of newPages) newObjs[pg.uid] = [];
    // Pinned page-wide objects stay on their page, when that page survives.
    for (const pg of pages) {
        if (!roleIdsByUid[pg.uid] || !newObjs[pg.uid]) continue;
        for (const rid of roleIdsByUid[pg.uid]) { const ro = nextById[rid]; if (ro) newObjs[pg.uid].push(ro); }
    }
    for (const p of placed) {
        const pgUid = newPages[p.pageIdx].uid;
        (newObjs[pgUid] ||= []).push(p.y === p.o.y ? p.o : { ...p.o, y: p.y });
    }
    return { pages: newPages, objectsByUid: newObjs };
}

function spreadDocumentWide(state, uid, target, patch) {
    const rule = DOCUMENT_WIDE_ROLES[target?.role];
    if (!rule) return null;

    const source = state.pages.find((p) => p.uid === uid);
    if (!source?.width || !source?.height) return null;

    const carried = { ...patch };
    for (const key of rule.keep) delete carried[key];
    // `id` is the one field that must never travel: two objects sharing an id
    // makes selection ambiguous and undo unpredictable.
    delete carried.id;
    if (!Object.keys(carried).length) return null;

    // Where the box ends up, as fractions of the page it ends up on.
    const after = { ...target, ...patch };
    const fractions = {
        x: after.x / source.width,
        y: after.y / source.height,
        w: after.w / source.width,
        h: after.h / source.height,
    };
    const geometry = new Set(['x', 'y', 'w', 'h']);

    const next = {};
    // What each twin held before, so a rejected change can put every page back
    // the way it was rather than levelling them all to one value.
    const before = {};
    for (const page of state.pages) {
        if (page.uid === uid) continue;
        const list = state.objectsByUid[page.uid];
        const twin = list?.find((o) => o.role === target.role);
        if (!twin) continue;

        const scaled = {};
        for (const [key, value] of Object.entries(carried)) {
            if (!geometry.has(key)) { scaled[key] = value; continue; }
            const span = key === 'x' || key === 'w' ? page.width : page.height;
            scaled[key] = fractions[key] * span;
        }
        // Type size is a length too: left alone, a watermark set to 96pt on A4
        // would stay 96pt on a page half the width.
        if (carried.fontSize != null && page.width !== source.width) {
            scaled.fontSize = carried.fontSize * (page.width / source.width);
        }
        before[page.uid] = {
            id: twin.id,
            values: Object.fromEntries(Object.keys(scaled).map((key) => [key, twin[key]])),
        };
        next[page.uid] = list.map((o) => (o.id === twin.id ? { ...o, ...scaled } : o));
    }
    return Object.keys(next).length ? { next, before } : null;
}

const snapshotOf = (state) => ({
    pages: state.pages,
    objectsByUid: state.objectsByUid,
    revisions: state.revisions,
    // The document body, so a command that rewrites the FILE — taking out the
    // watermark it arrived with — is undoable like everything else. Both are
    // references, not copies: an ordinary text edit adds no bytes to history,
    // and the only cost is that a pdf.js handle stays alive while some snapshot
    // still points at it (which is exactly what undo needs it to do).
    sourceBytes: state.sourceBytes,
    pdf: state.pdf,
    // The outline is part of the document, so undo has to carry it too —
    // otherwise deleting a heading and pressing undo brings the text back and
    // leaves the bookmark pointing at nothing.
    bookmarks: state.bookmarks,
});

/**
 * A paragraph opened for editing and not yet changed is PENDING: the box is
 * there for the caret, but the page still shows the PDF's own rendering — the
 * replacement (`replaces`, which strips the source words) and its cover patch
 * wait in `pending`. A reflowed copy can never land every word where the
 * authoring app put it, so swapping it in on a mere click made the paragraph
 * shift; deferring the swap to the first real change means a click moves
 * nothing, and leaving without a change leaves the page untouched.
 */
const without = (byUid, uid, id) => ({
    ...byUid,
    [uid]: (byUid[uid] || []).filter((o) => o.id !== id),
});

/** The pending box made real: its cover goes in just beneath it. */
function activatePending(byUid, uid, id) {
    const list = byUid[uid] || [];
    const at = list.findIndex((o) => o.id === id);
    if (at < 0 || !list[at].pending) return byUid;
    const { pending, ...box } = list[at];
    const next = [...list];
    next.splice(at, 1, ...(pending.patch ? [pending.patch] : []), { ...box, replaces: pending.replaces });
    return { ...byUid, [uid]: next };
}

/**
 * Document mode: the body paragraphs in reading order — page by page, top to
 * bottom, then left to right. A selection that crosses paragraphs is two points
 * in this list. Headers, footers and page numbers (`role`) are not body text.
 */
export function docFlowList(state) {
    const list = [];
    for (const page of state.pages) {
        const flows = (state.objectsByUid[page.uid] || [])
            .filter((o) => !o.role && (o.type === OBJ.TEXT || o.type === OBJ.NOTE))
            .sort((a, b) => (a.y - b.y) || (a.x - b.x));
        for (const obj of flows) list.push({ uid: page.uid, obj });
    }
    return list;
}

/**
 * `docFlowList` with the document's tables in their places too: what a
 * selection copies. A table is not text, so a selection never ends in one,
 * but one that runs past it carries it along, as Word's does.
 */
function docBlockList(state) {
    const list = [];
    for (const page of state.pages) {
        const blocks = (state.objectsByUid[page.uid] || [])
            .filter((o) => !o.role && (o.type === OBJ.TEXT || o.type === OBJ.NOTE || o.type === OBJ.TABLE))
            .sort((a, b) => (a.y - b.y) || (a.x - b.x));
        for (const obj of blocks) list.push({ uid: page.uid, obj });
    }
    return list;
}

/**
 * The blocks a document selection covers, `{ list, from, to, ends }`: `list`
 * from `docBlockList`, `from`/`to` indices into it. A selection from the very
 * start or to the very end (Select All) takes the tables beyond its text too.
 */
function selectedBlocks(state, sel) {
    const flow = docFlowList(state);
    const ends = orderDocSelection(sel, flow);
    if (!ends) return null;
    const list = docBlockList(state);
    const first = flow[ends.from.i].obj;
    const last = flow[ends.to.i].obj;
    let from = list.findIndex((e) => e.obj.id === first.id);
    let to = list.findIndex((e) => e.obj.id === last.id);
    if (ends.from.i === 0 && ends.from.off === 0) from = 0;
    if (ends.to.i === flow.length - 1 && ends.to.off >= String(last.text ?? '').length) to = list.length - 1;
    return { list, from, to, ends: { from: first.id, fromOff: ends.from.off, to: last.id, toOff: ends.to.off } };
}

/** The ids of the tables a document selection carries (for its highlight). */
export function docSelectionTableIds(state, sel = state.docSelection) {
    const range = selectedBlocks(state, sel);
    if (!range) return [];
    return range.list.slice(range.from, range.to + 1).filter((e) => e.obj.type === OBJ.TABLE).map((e) => e.obj.id);
}

/** A selection's two ends, in document order: `{ from, to }` as `{ i, off }`, or null. */
export function orderDocSelection(sel, list) {
    if (!sel) return null;
    const ia = list.findIndex((e) => e.obj.id === sel.anchor.id);
    const ib = list.findIndex((e) => e.obj.id === sel.focus.id);
    if (ia < 0 || ib < 0) return null;
    const a = { i: ia, off: sel.anchor.offset };
    const b = { i: ib, off: sel.focus.offset };
    return (a.i < b.i || (a.i === b.i && a.off <= b.off)) ? { from: a, to: b } : { from: b, to: a };
}

/** The plain text a cross-paragraph selection covers, paragraphs on their own lines. */
export function docSelectionText(state, sel = state.docSelection) {
    const list = docFlowList(state);
    const ends = orderDocSelection(sel, list);
    if (!ends) return '';
    const parts = [];
    for (let i = ends.from.i; i <= ends.to.i; i += 1) {
        const text = String(list[i].obj.text ?? '');
        parts.push(text.slice(i === ends.from.i ? ends.from.off : 0, i === ends.to.i ? ends.to.off : text.length));
    }
    return parts.join('\n');
}

/**
 * Document mode's own clipboard, beside the system one.
 *
 * The system clipboard gets plain text (anything can paste it). This keeps the
 * same copy as PARAGRAPHS — each one's text, runs, box style and the space
 * above it — so pasting it back into a document rebuilds headings, sizes and
 * paragraph spacing instead of running everything into one box. It is used
 * only when the pasted text is exactly what was copied here.
 */
let docClipboard = null;

const PARAGRAPH_GEOMETRY = ['id', 'x', 'y', 'w', 'h', 'text', 'marks', 'page'];
const paragraphStyleOf = (obj) => Object.fromEntries(
    Object.entries(obj).filter(([key]) => !PARAGRAPH_GEOMETRY.includes(key)),
);

/** Marks of `[start, end)` of a box, moved to start at 0. */
const sliceMarks = (marks = [], start, end) => (marks || [])
    .map((m) => ({ start: Math.max(m.start, start) - start, end: Math.min(m.end, end) - start, style: m.style }))
    .filter((m) => m.end > m.start);

/** One box's `[start, end)` as a copied paragraph. */
export function boxRangeParagraph(obj, start, end) {
    const full = String(obj.text ?? '');
    return {
        text: full.slice(start, end),
        marks: sliceMarks(obj.marks, start, end),
        style: paragraphStyleOf(obj),
        x: obj.x,
        w: obj.w,
        gapBefore: 0,
    };
}

/** A whole table as a copied block (see `docSelectionParagraphs`). */
export function tableBlock(obj, gapBefore = 0) {
    const { id, y, page, reviewMark, pendingDelete, ...table } = obj;
    return { table, x: obj.x, w: obj.w, gapBefore };
}

/**
 * A document selection as copied blocks: paragraphs (text, runs, box style,
 * spacing above) and, between them, whole tables as `{ table }` blocks.
 */
export function docSelectionParagraphs(state, sel = state.docSelection) {
    const range = selectedBlocks(state, sel);
    if (!range) return [];
    const { list, ends } = range;
    const paragraphs = [];
    for (let i = range.from; i <= range.to; i += 1) {
        const { obj } = list[i];
        const prev = i > range.from ? list[i - 1] : null;
        const sameColumn = prev && prev.uid === list[i].uid;
        const gapBefore = prev ? (sameColumn ? Math.max(0, obj.y - (prev.obj.y + prev.obj.h)) : 8) : 0;
        if (obj.type === OBJ.TABLE) {
            paragraphs.push(tableBlock(obj, gapBefore));
            continue;
        }
        const full = String(obj.text ?? '');
        const start = obj.id === ends.from ? ends.fromOff : 0;
        const end = obj.id === ends.to ? ends.toOff : full.length;
        paragraphs.push({
            text: full.slice(start, end),
            marks: sliceMarks(obj.marks, start, end),
            style: paragraphStyleOf(obj),
            x: obj.x,
            w: obj.w,
            gapBefore,
        });
    }
    return paragraphs;
}

/** Copied blocks as plain text: paragraphs on their own lines, tables as tab-separated rows. */
export const docClipboardText = (paragraphs) => paragraphs.map(blockText).join('\n');

/** Remember copied paragraphs (a document selection by default), for `pasteDocParagraphs`. */
export function rememberDocClipboard(state, paragraphs = docSelectionParagraphs(state)) {
    if (!paragraphs.length) return;
    docClipboard = { text: docClipboardText(paragraphs), paragraphs };
}

/** The remembered paragraphs, when `text` is what they were copied as. */
export function docClipboardFor(text) {
    return docClipboard && docClipboard.text === text ? docClipboard : null;
}

/**
 * Document mode: an object dragged to a new spot in the flow (same page or
 * another), the way Word moves a table — the gap it left closes, and room
 * opens where it lands: it goes between the block whose middle is above the
 * POINTER (`dropY`, where Word shows its insertion mark — not the object's own
 * top edge) and the one below, with the spacing those two had, and keeps its
 * column (`origin.x`). Pagination then settles the rest.
 */
function placeInFlow(byUid, fromUid, toUid, id, pos, origin, dropY = pos.y) {
    const src = byUid[fromUid] || [];
    const obj = src.find((o) => o.id === id);
    if (!obj) return byUid;
    const out = { ...byUid };
    const flowsOf = (list) => list.filter((o) => !o.role && o.id !== id);

    // 1. Close the gap it left: what was below moves up by its height + the gap after it.
    //    It leaves this page's list (step 2 adds it where it lands, this page or another).
    const below = flowsOf(src).filter((o) => o.y >= origin.y + obj.h - 0.5);
    const shift = below.length ? Math.min(...below.map((o) => o.y)) - origin.y : 0;
    out[fromUid] = src
        .filter((o) => o.id !== id)
        .map((o) => (shift && below.includes(o) ? { ...o, y: o.y - shift } : o));

    // 2. Open room where it lands.
    const dst = (out[toUid] || []).filter((o) => o.id !== id);
    const flows = flowsOf(dst);
    const before = flows.filter((o) => o.y + o.h / 2 <= dropY);
    const after = flows.filter((o) => o.y + o.h / 2 > dropY);
    const prevBottom = before.length ? Math.max(...before.map((o) => o.y + o.h)) : null;
    const nextTop = after.length ? Math.min(...after.map((o) => o.y)) : null;
    const gap = prevBottom != null && nextTop != null ? Math.max(4, nextTop - prevBottom) : 8;
    const y = prevBottom != null ? prevBottom + gap : (nextTop ?? pos.y);
    const moved = { ...obj, x: origin.x, y };
    const push = nextTop != null ? Math.max(0, y + obj.h + gap - nextTop) : 0;
    out[toUid] = [...dst.map((o) => (push && after.includes(o) ? { ...o, y: o.y + push } : o)), moved];
    return out;
}

/** Where a pending box lives, if `id` is one. */
function findPending(state, id) {
    if (!id) return null;
    for (const [uid, list] of Object.entries(state.objectsByUid)) {
        const obj = (list || []).find((o) => o.id === id);
        if (obj) return obj.pending ? { uid, obj } : null;
    }
    return null;
}


/**
 * Putting a rejected edit back on EVERY page it reached.
 *
 * A document-wide stamp is edited once and spread to its twin on each of the
 * other pages, but only one revision is recorded — the one the user actually
 * made. Restoring just that one leaves the watermark back where it was on page
 * 5 and still moved on the other twenty-four, which is precisely the split this
 * whole role exists to prevent.
 *
 * So the restored values are spread again, through the same transform that
 * carried the edit out.
 */
function restoreAcrossPages(state, objectsByUid, revision, restored) {
    if (!DOCUMENT_WIDE_ROLES[restored?.role]) return objectsByUid;

    // What was recorded when the edit spread: each page's own previous values.
    // Levelling every page to the source page's restored position instead would
    // lose a difference the user may have meant — a page of another size holds
    // the same stamp at different points.
    if (revision.spreadBefore) {
        const next = { ...objectsByUid };
        for (const [pageUid, entry] of Object.entries(revision.spreadBefore)) {
            const list = next[pageUid];
            if (!list) continue;
            next[pageUid] = list.map((o) => (o.id === entry.id ? { ...o, ...entry.values } : o));
        }
        return next;
    }

    // A revision from before that was recorded: fall back to spreading the
    // restored values, which at least leaves the document consistent.
    const spread = spreadDocumentWide(
        { ...state, objectsByUid },
        revision.uid,
        restored,
        revision.before,
    );
    return spread ? { ...objectsByUid, ...spread.next } : objectsByUid;
}

export const usePdfEditorStore = create((set, get) => ({
    ...emptyDoc,

    ready: false,
    loading: false,
    error: null,
    dirty: false,

    activeTool: TOOLS.SELECT,
    toolSettings: structuredClone(DEFAULT_TOOL_SETTINGS),

    // Document mode: the editor was opened to edit an AI-generated document (not a real
    // PDF). A plain click on a text box opens it for editing straight away, and the
    // PDF-original-text tool (Edit text) is hidden since there is no source text layer.
    documentMode: false,
    // { top, bottom } in points when the document brings its own margins (null → derived from the layout)
    docMargins: null,
    setDocumentMode: (v) => set({ documentMode: !!v }),
    // PDF edit tool: Edit Text is always on — with the Select tool, a click on
    // any paragraph of the page opens it for editing, and a click on a text box
    // types into it, as in document mode. The ribbon drops its Edit button.
    clickToEditText: false,

    selectedId: null,
    editingId: null,
    // Which cell of the editing table is open ({ r, c }), or null for a normal text box.
    editingCell: null,

    zoom: 1,
    /** null = use `zoom` verbatim, so a document opens at a true 100%. */
    fitMode: null,
    currentPage: 0,
    textSelection: { start: 0, end: 0 },
    // Document mode: a selection that crosses paragraphs, `{ anchor, focus }`
    // with each end `{ id, offset }` (see `docFlowList`). Painted over the
    // canvas text; while it is set no box is open for typing.
    docSelection: null,
    // An object being dragged over ANOTHER page: `{ uid, obj }`, the copy that
    // page paints where the drop would put it (the dragged object itself is
    // clipped at its own page's edge).
    dragGhost: null,
    scrollNonce: 0,
    viewMode: 'continuous',

    showThumbnails: true,
    showInspector: true,
    // The ruler is a measuring aid, not furniture: worth a switch, and on
    // by default because that is how the page has always opened.
    showRuler: true,
    /** Word's View → Gridlines: a measuring grid over the page, never exported. */
    showGrid: false,
    /**
     * Word's four views. `print` is the pages as paper; `web` is one long
     * column at full width; `draft` strips the page furniture and the pictures
     * so a long document scrolls quickly; `outline` puts the structure up
     * instead of the pages.
     */
    displayMode: 'print',
    /** Word's View → Split: two panes over one document, scrolling apart. */
    splitView: false,
    splitRatio: 0.5,
    /** Word's Review → Spelling and Language, as the browser can do them. */
    language: 'bn',
    /** An object cut or copied inside the editor, waiting to be pasted. */
    clipboard: null,
    /** Files to travel inside the exported PDF (Word's Insert → Object). */
    /**
     * The document outline. Entries belong to the document, not to a page's
     * objects, because that is what they are in the file too — a tree in the
     * catalogue that the reader shows down the side.
     */
    bookmarks: [],
    showBookmarks: false,
    /**
     * Track changes. While it is on, every edit is recorded rather than simply
     * done: additions and edits are marked up on the page, and a deletion is
     * held — the object stays, struck through — until somebody accepts it.
     */
    /** Page margins — guides on screen, and where the running text sits. */
    margins: { top: 72, right: 72, bottom: 72, left: 72 },
    marginPreset: 'normal',
    showMargins: false,
    trackChanges: false,
    revisions: [],
    showReviewing: false,
    /** The accessibility check: what it found, and whether it has been run. */
    a11yIssues: [],
    a11yChecked: false,
    showAccessibility: false,
    /** The style Format painter picked up, waiting to be brushed onto something. */
    styleBrush: null,
    showSearch: false,
    pagesModal: false,
    signModal: false,
    emojiModal: false,
    symbolModal: false,
    equationModal: false,
    /** `{ uid, id, isNew }` while a link's target is being entered. */
    linkDraft: null,

    search: { query: '', hits: [], index: 0, running: false },

    past: [],
    future: [],

    /* ------------------------------------------------------------ document */

    loadDocument: ({ fileName, sourceBytes, pdf, pages }) => set({
        fileName,
        sourceBytes,
        pdf,
        pages,
        objectsByUid: {},
        ready: true,
        loading: false,
        error: null,
        dirty: false,
        past: [],
        future: [],
        selectedId: null,
        editingId: null,
        currentPage: 0,
        search: { query: '', hits: [], index: 0, running: false },
        // Transient chrome must not survive a reopen — the store outlives the
        // overlay (the modal renders null instead of unmounting).
        showSearch: false,
        pagesModal: false,
        signModal: false,
        linkDraft: null,
        activeTool: TOOLS.SELECT,
        zoom: 1,
        fitMode: null,
    }),

    resetDocument: () => set({ ...emptyDoc, ready: false, dirty: false, past: [], future: [] }),

    setLoading: (loading) => set({ loading }),
    setError: (error) => set({ error, loading: false }),

    /* ------------------------------------------------------------- history */

    /**
     * Swap the document the editor is working on, keeping everything drawn on
     * top of it.
     *
     * Used by the watermark remover: the pages, their geometry and every object
     * on them are unchanged — what changed is the file underneath, so only the
     * bytes and the pdf.js handle move. The canvas and the export both read
     * `sourceBytes`, so one swap reaches the screen and the saved file alike.
     *
     * Goes through history like any other edit, so undo brings the watermark
     * back. That is why the outgoing pdf.js handle is NOT destroyed here: the
     * snapshot still points at it, and undo has to be able to draw from it.
     */
    replaceSource: ({ sourceBytes, pdf }) => set((state) => ({
        sourceBytes,
        pdf,
        dirty: true,
        past: [...state.past, snapshotOf(state)].slice(-HISTORY_LIMIT),
        future: [],
    })),

    commit: (mutator) => set((state) => {
        const next = mutator(state);
        if (!next) return {};
        return {
            ...next,
            dirty: true,
            past: [...state.past, snapshotOf(state)].slice(-HISTORY_LIMIT),
            future: [],
        };
    }),

    undo: () => set((state) => {
        if (!state.past.length) return {};
        const previous = state.past[state.past.length - 1];
        return {
            ...previous,
            past: state.past.slice(0, -1),
            future: [snapshotOf(state), ...state.future].slice(0, HISTORY_LIMIT),
            selectedId: null,
            editingId: null,
            docSelection: null,
            dirty: true,
        };
    }),

    redo: () => set((state) => {
        if (!state.future.length) return {};
        const [next, ...rest] = state.future;
        return {
            ...next,
            past: [...state.past, snapshotOf(state)].slice(-HISTORY_LIMIT),
            future: rest,
            selectedId: null,
            editingId: null,
            docSelection: null,
            dirty: true,
        };
    }),

    /* --------------------------------------------------------------- tools */

    setTool: (tool) => set((state) => {
        const left = findPending(state, state.editingId);
        return {
            ...(left ? { objectsByUid: without(state.objectsByUid, left.uid, left.obj.id) } : {}),
            activeTool: tool,
            selectedId: tool === TOOLS.SELECT && !left ? state.selectedId : null,
            editingId: null,
        };
    }),

    updateToolSettings: (tool, patch) => set((state) => ({
        toolSettings: {
            ...state.toolSettings,
            [tool]: { ...state.toolSettings[tool], ...patch },
        },
    })),

    /* ------------------------------------------------------------- objects */

    addObject: (uid, object, { select = true } = {}) => {
        get().commit((state) => {
            const tracked = state.trackChanges
                ? { ...object, reviewMark: 'add' }
                : object;
            return {
                objectsByUid: {
                    ...state.objectsByUid,
                    [uid]: [...(state.objectsByUid[uid] || []), tracked],
                },
                revisions: state.trackChanges
                    ? [...state.revisions, newRevision('add', uid, object.id)]
                    : state.revisions,
            };
        });
        if (select) set({ selectedId: object.id });
        return object.id;
    },

    /**
     * Add several objects as a single undoable step.
     *
     * "Edit text" makes two at once: the replacement box, and the patch that
     * stands in for the paragraph underneath it. Added one at a time they took
     * one history entry each, so a single undo removed the box and left the
     * patch sitting on the page — a blank rectangle with nothing on top.
     */
    /**
     * Open a paragraph for editing without changing the page (see
     * `activatePending`). No history, no dirty flag: nothing has happened yet.
     */
    addPendingEdit: (uid, box, { replaces, patch }) => set((state) => ({
        objectsByUid: {
            ...state.objectsByUid,
            [uid]: [...(state.objectsByUid[uid] || []), { ...box, replaces: undefined, pending: { replaces, patch } }],
        },
        selectedId: box.id,
    })),

    addObjects: (uid, objects, { select } = {}) => {
        if (!objects?.length) return;
        get().commit((state) => ({
            objectsByUid: {
                ...state.objectsByUid,
                [uid]: [...(state.objectsByUid[uid] || []), ...objects],
            },
        }));
        if (select) set({ selectedId: select });
    },

    /**
     * Patch an object. Live drags pass `transient` so the history stack keeps
     * one entry per gesture instead of one per pointer move.
     */
    updateObject: (uid, id, patch, { transient = false } = {}) => {
        // The first real change to a pending paragraph makes the replacement
        // real — one undo step, back to the page as it was before the click.
        if ((get().objectsByUid[uid] || []).some((o) => o.id === id && o.pending)) {
            set((state) => ({
                objectsByUid: activatePending(state.objectsByUid, uid, id),
                past: [...state.past, snapshotOf({ ...state, objectsByUid: without(state.objectsByUid, uid, id) })].slice(-HISTORY_LIMIT),
                future: [],
                dirty: true,
            }));
        }
        const apply = (state) => {
            const list = state.objectsByUid[uid] || [];
            const target = list.find((o) => o.id === id);
            // An object added during this review does not also need an "edited"
            // record — it is new, and the addition already covers it. Neither
            // does a second edit to something already on the list: the first
            // record holds the state to go back to.
            const track = state.trackChanges
                && target
                && target.reviewMark !== 'add'
                && !state.revisions.some((r) => r.objectId === id && r.kind !== 'delete');
            const before = track
                ? Object.fromEntries(Object.keys(patch).map((key) => [key, target[key]]))
                : null;

            // A watermark, a header, a page number: one decision about the
            // document that happens to be drawn on every page. Editing the copy
            // under the pointer has to edit all of them, or the user is left
            // repeating the same drag twenty-five times.
            const spread = target ? spreadDocumentWide(state, uid, target, patch) : null;

            // Document-mode reflow: when an auto-height text box changes a property that
            // changes its height (font size, line height, font, the text itself), grow or
            // shrink the box to fit its text — then re-flow the WHOLE document so the boxes
            // below move with it and overflow spills onto a new page (word-processor style).
            let heightPatch = null;
            let reflow = false;
            if (target && !target.role) {
                if (
                    state.documentMode && target.type === OBJ.TEXT
                    && target.autoHeight !== false && patch.autoHeight !== false
                ) {
                    const H_KEYS = ['fontSize', 'lineHeight', 'fontFamily', 'fontStack', 'fontId', 'text', 'marks', 'w'];
                    if (H_KEYS.some((k) => k in patch)) {
                        const merged = { ...target, ...patch };
                        const newH = Math.max(
                            (merged.fontSize || 0) * (merged.lineHeight || 1) + TEXT_PAD * 2,
                            measuredTextHeight(merged),
                        );
                        heightPatch = { h: newH };
                        reflow = Math.abs(newH - (target.h || 0)) > 0.01;
                    }
                } else if (target.type === OBJ.TABLE) {
                    // A table's row heights follow its cell text (+ width). Recompute them in
                    // BOTH modes so the grid always draws correctly; only flow in document mode.
                    const T_KEYS = ['cells', 'rows', 'cols', 'w', 'fontSize', 'lineHeight', 'padX', 'padY', 'fontStack', 'fontFamily', 'colFracs', 'merges', 'cellStyles', 'rowMin'];
                    if (T_KEYS.some((k) => k in patch)) {
                        const merged = recomputeTable({ ...target, ...patch });
                        heightPatch = { rowHeights: merged.rowHeights, h: merged.h };
                        reflow = state.documentMode && Math.abs(merged.h - (target.h || 0)) > 0.01;
                    }
                }
            }

            const patchedByUid = {
                ...state.objectsByUid,
                ...(spread?.next || {}),
                [uid]: list.map((o) => (o.id === id
                    ? { ...o, ...patch, ...(heightPatch || {}), ...(track ? { reviewMark: 'edit' } : {}) }
                    : o)),
            };

            // Re-flow + re-paginate the document after a height change. Gaps come from the
            // pre-edit layout (state.objectsByUid); new heights come from patchedByUid.
            const paged = reflow ? paginateDocument(state.pages, state.objectsByUid, patchedByUid, state.docMargins) : null;

            return {
                objectsByUid: paged ? paged.objectsByUid : patchedByUid,
                ...(paged ? {
                    pages: paged.pages,
                    currentPage: Math.min(state.currentPage, paged.pages.length - 1),
                } : {}),
                revisions: track
                    ? [...state.revisions, {
                        ...newRevision('edit', uid, id, before),
                        // Every page this edit also reached, and what it held.
                        spreadBefore: spread?.before || null,
                    }]
                    : state.revisions,
            };
        };
        if (transient) set((state) => ({ ...apply(state), dirty: true }));
        else get().commit(apply);
    },

    /** Snapshot the current state without changing it (start of a drag). */
    beginGesture: () => set((state) => ({
        past: [...state.past, snapshotOf(state)].slice(-HISTORY_LIMIT),
        future: [],
    })),

    // An "Edit text" box owns the patch that stands in for the paragraph it
    // replaced, so deleting the box takes the patch with it — otherwise a blank
    // rectangle is left sitting where the original words used to be.
    removeObject: (uid, id) => get().commit((state) => {
        const target = (state.objectsByUid[uid] || []).find((o) => o.id === id);
        const noteId = target?.noteId || null;

        // While changes are tracked a deletion is a proposal: the object stays,
        // struck through, until somebody accepts it. Deleting something that was
        // only added during this review is the exception — there is nothing to
        // propose, so it simply goes, and its record with it.
        if (state.trackChanges && target) {
            if (target.reviewMark === 'add') {
                return {
                    objectsByUid: {
                        ...state.objectsByUid,
                        [uid]: (state.objectsByUid[uid] || []).filter((o) => o.id !== id && o.patchFor !== id),
                    },
                    revisions: state.revisions.filter((r) => r.objectId !== id),
                    selectedId: state.selectedId === id ? null : state.selectedId,
                    editingId: state.editingId === id ? null : state.editingId,
                };
            }
            return {
                objectsByUid: {
                    ...state.objectsByUid,
                    [uid]: (state.objectsByUid[uid] || []).map((o) => (
                        o.id === id ? { ...o, pendingDelete: true } : o
                    )),
                },
                revisions: [...state.revisions, newRevision('delete', uid, id)],
                selectedId: state.selectedId === id ? null : state.selectedId,
                editingId: state.editingId === id ? null : state.editingId,
            };
        }

        let objectsByUid = {
            ...state.objectsByUid,
            [uid]: (state.objectsByUid[uid] || []).filter((o) => o.id !== id && o.patchFor !== id),
        };

        // A footnote is a marker and a note: deleting half of one would leave
        // either a number pointing at nothing or a note nobody can reach. Then
        // everything after it renumbers, which is the part nobody will do by
        // hand on the fourteenth note of page nine.
        if (noteId) {
            for (const [pageUid, list] of Object.entries(objectsByUid)) {
                objectsByUid[pageUid] = (list || []).filter((o) => o.noteId !== noteId);
            }
            objectsByUid = layoutNotes(state.pages, objectsByUid);
        }

        // Deleting the watermark takes the watermark off the document, not off
        // page 3. It is the same object on every page, and leaving twenty-four
        // of it behind is the thing this whole role exists to avoid.
        if (DOCUMENT_WIDE_ROLES[target?.role]) {
            for (const [pageUid, list] of Object.entries(objectsByUid)) {
                objectsByUid[pageUid] = (list || []).filter((o) => o.role !== target.role);
            }
        }

        return {
            objectsByUid,
            selectedId: state.selectedId === id ? null : state.selectedId,
            editingId: state.editingId === id ? null : state.editingId,
        };
    }),

    duplicateObject: (uid, id) => {
        const state = get();
        const source = (state.objectsByUid[uid] || []).find((o) => o.id === id);
        if (!source) return;
        const copy = cloneObject(source);
        state.addObject(uid, copy);
    },

    reorderObject: (uid, id, direction) => get().commit((state) => {
        const list = [...(state.objectsByUid[uid] || [])];
        const from = list.findIndex((o) => o.id === id);
        if (from < 0) return null;
        const to = direction === 'front' ? list.length - 1
            : direction === 'back' ? 0
                : Math.min(list.length - 1, Math.max(0, from + direction));
        if (to === from) return null;
        const [item] = list.splice(from, 1);
        list.splice(to, 0, item);
        return { objectsByUid: { ...state.objectsByUid, [uid]: list } };
    }),

    selectObject: (id) => set((state) => {
        const left = findPending(state, state.editingId);
        return {
            ...(left ? { objectsByUid: without(state.objectsByUid, left.uid, left.obj.id) } : {}),
            selectedId: id,
            editingId: null,
        };
    }),
    // `at` = the viewport point of the click that opened the box (document mode places the
    // caret there, and a click on a [placeholder] selects the whole variable).
    editAt: null,
    // Leaving a pending paragraph untouched drops it: the page never changed.
    setEditing: (id, cell = null, at = null) => set((state) => {
        const left = state.editingId !== id ? findPending(state, state.editingId) : null;
        return {
            ...(left ? { objectsByUid: without(state.objectsByUid, left.uid, left.obj.id) } : {}),
            editingId: id,
            editingCell: id ? cell : null,
            editAt: id ? at : null,
            selectedId: id,
            textSelection: { start: 0, end: 0 },
        };
    }),
    setEditingCell: (cell) => set({ editingCell: cell }),

    // Re-flow + re-paginate the whole document in its current state (document mode only) —
    // used after inserting a block (e.g. a table) so it slots into the flow with no overlap.
    setDragGhost: (dragGhost) => set({ dragGhost }),

    /**
     * Finish a drag that ended over another page: the object leaves `fromUid`
     * and joins `toUid` at `pos` (its top-left there). No history entry of its
     * own — the drag opened one when it started. In document mode the pages
     * then re-flow, so the object takes its place in the reading order where
     * it was dropped, as in Word.
     */
    moveObjectToPage: (fromUid, toUid, id, pos, origin, dropY) => set((state) => {
        const obj = (state.objectsByUid[fromUid] || []).find((o) => o.id === id);
        if (!obj || fromUid === toUid) return {};
        if (!state.documentMode) {
            return {
                objectsByUid: {
                    ...state.objectsByUid,
                    [fromUid]: (state.objectsByUid[fromUid] || []).filter((o) => o.id !== id),
                    [toUid]: [...(state.objectsByUid[toUid] || []), { ...obj, x: pos.x, y: pos.y }],
                },
                dirty: true,
                dragGhost: null,
                selectedId: id,
            };
        }
        const byUid = placeInFlow(state.objectsByUid, fromUid, toUid, id, pos, origin || obj, dropY);
        const paged = paginateDocument(state.pages, byUid, byUid, state.docMargins);
        return {
            pages: paged.pages,
            objectsByUid: paged.objectsByUid,
            currentPage: Math.min(state.currentPage, paged.pages.length - 1),
            dirty: true,
            dragGhost: null,
            selectedId: id,
        };
    }),

    /** Document mode, after a drag on one page: the object takes its new place in the flow. */
    settleAfterDrag: (uid, id, origin, dropY) => set((state) => {
        if (!state.documentMode) return {};
        const obj = (state.objectsByUid[uid] || []).find((o) => o.id === id);
        const byUid = obj ? placeInFlow(state.objectsByUid, uid, uid, id, { x: obj.x, y: obj.y }, origin, dropY) : state.objectsByUid;
        const paged = paginateDocument(state.pages, byUid, byUid, state.docMargins);
        return {
            pages: paged.pages,
            objectsByUid: paged.objectsByUid,
            currentPage: Math.min(state.currentPage, paged.pages.length - 1),
        };
    }),

    reflowDocument: () => get().commit((state) => {
        if (!state.documentMode) return {};
        const paged = paginateDocument(state.pages, state.objectsByUid, state.objectsByUid, state.docMargins);
        return {
            pages: paged.pages,
            objectsByUid: paged.objectsByUid,
            currentPage: Math.min(state.currentPage, paged.pages.length - 1),
        };
    }),

    /* --------------------------------------------------------------- pages */

    rotatePage: (uid, delta) => get().commit((state) => ({
        pages: state.pages.map((p) => (p.uid === uid
            ? { ...p, userRotation: (((p.userRotation + delta) % 360) + 360) % 360 }
            : p)),
    })),

    rotateAllPages: (delta) => get().commit((state) => ({
        pages: state.pages.map((p) => ({
            ...p,
            userRotation: (((p.userRotation + delta) % 360) + 360) % 360,
        })),
    })),

    deletePages: (uids) => get().commit((state) => {
        const drop = new Set(uids);
        const pages = state.pages.filter((p) => !drop.has(p.uid));
        if (!pages.length) return null;
        const objectsByUid = { ...state.objectsByUid };
        for (const uid of drop) delete objectsByUid[uid];
        return { pages, objectsByUid, currentPage: Math.min(state.currentPage, pages.length - 1) };
    }),

    duplicatePages: (uids) => get().commit((state) => {
        const pages = [];
        const objectsByUid = { ...state.objectsByUid };
        for (const page of state.pages) {
            pages.push(page);
            if (uids.includes(page.uid)) {
                const uid = newId('pg');
                pages.push({ ...page, uid });
                const objects = state.objectsByUid[page.uid];
                if (objects?.length) {
                    objectsByUid[uid] = objects.map((o) => ({ ...o, id: newId(o.type) }));
                }
            }
        }
        return { pages, objectsByUid };
    }),

    insertBlankPage: (afterIndex, size) => get().commit((state) => {
        const reference = state.pages[afterIndex] || state.pages[0];
        const page = {
            uid: newId('pg'),
            sourceIndex: null,
            width: size?.width || reference?.width || 595.28,
            height: size?.height || reference?.height || 841.89,
            baseRotation: 0,
            userRotation: 0,
        };
        const pages = [...state.pages];
        pages.splice(afterIndex + 1, 0, page);
        return { pages, currentPage: afterIndex + 1 };
    }),

    movePage: (from, to) => get().commit((state) => {
        if (from === to || from < 0 || to < 0 || from >= state.pages.length || to >= state.pages.length) {
            return null;
        }
        const pages = [...state.pages];
        const [page] = pages.splice(from, 1);
        pages.splice(to, 0, page);
        return { pages, currentPage: to };
    }),

    setPages: (pages) => get().commit(() => ({ pages })),

    /* ---------------------------------------------------------------- view */

    setZoom: (zoom) => set({ zoom: Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom)), fitMode: null }),
    zoomIn: () => set((state) => ({
        zoom: ZOOM_STEPS.find((z) => z > state.zoom + 0.001) ?? ZOOM_MAX,
        fitMode: null,
    })),
    zoomOut: () => set((state) => ({
        zoom: [...ZOOM_STEPS].reverse().find((z) => z < state.zoom - 0.001) ?? ZOOM_MIN,
        fitMode: null,
    })),
    setFitMode: (fitMode) => set({ fitMode }),
    // Where the caret is inside the box being edited, so the options bar can
    // style a selection instead of the whole box.
    setTextSelection: (textSelection) => set({ textSelection }),
    setDocSelection: (docSelection) => set(docSelection
        ? { docSelection, editingId: null, editingCell: null }
        : { docSelection: null }),

    /**
     * Replace a cross-paragraph selection with `insert` (empty: delete it), as
     * one undo step. As in Word, what is left of the last paragraph joins the
     * first; the paragraphs between go; the document then re-flows so nothing
     * leaves a hole. The tail keeps its own look — its box style is carried
     * over as marks, since it now lives in the first paragraph's box.
     * Returns where the caret belongs afterwards, `{ uid, id, offset }`.
     */
    /**
     * Paste remembered paragraphs (see `rememberDocClipboard`) into box `id`
     * over `[start, end)`, as Word does: the first pasted paragraph continues
     * the text before the caret, the last one carries the text after it, and
     * the ones between become paragraphs of their own — each with its own
     * look and the spacing it had. A copied table goes in as a table of its
     * own between them; text never joins one, so a table at either end has
     * the paragraph split around it (an empty one after it, as Word keeps).
     * One undo step; the document re-flows.
     * Returns the caret after the paste, `{ id, offset }`.
     */
    pasteDocParagraphs: ({ id, start, end }, blocks) => {
        const state = get();
        const list = docFlowList(state);
        const at = list.findIndex((e) => e.obj.id === id);
        if (at < 0 || !blocks?.length) return null;
        const { uid, obj: box } = list[at];
        const text = String(box.text ?? '');
        const head = text.slice(0, start);
        const tail = text.slice(end);
        const paragraphs = [...(blocks || [])];
        const blank = (gapBefore) => ({ text: '', marks: [], style: paragraphStyleOf(box), x: box.x, w: box.w, gapBefore });
        if (paragraphs[0]?.table && head) paragraphs.unshift(blank(0));
        if (paragraphs[paragraphs.length - 1]?.table) paragraphs.push(blank(8));
        const tableOf = (p) => ({ ...p.table, id: newId(OBJ.TABLE), x: p.x, w: p.w, y: 0 });
        const fit = (o) => (o.type === OBJ.TABLE ? o
            : { ...o, h: Math.max(o.fontSize * (o.lineHeight || 1.35) + TEXT_PAD * 2, measuredTextHeight(o)) });
        // `runs` of a source laid onto `target` at `offset`, as marks.
        const overlay = (target, source, offset) => {
            let out = target;
            for (const run of runsOf(source)) {
                if (!run.text.length) continue;
                out = { ...out, marks: markRange(out, offset + run.start, offset + run.start + run.text.length, runStyleOf(run.style)) };
            }
            return out;
        };
        const asObject = (p) => ({ ...p.style, text: p.text, marks: p.marks });

        const n = paragraphs.length;
        const p0 = paragraphs[0];
        const pn = paragraphs[n - 1];
        const made = [];
        let first;
        let caret;

        if (n === 1) {
            first = { ...box, text: head + p0.text + tail, marks: shiftMarks(box.marks || [], start, end - start, p0.text.length) };
            first = overlay(first, asObject(p0), head.length);
            caret = { id: box.id, offset: head.length + p0.text.length };
        } else {
            // First: an empty head simply becomes the pasted paragraph, look and
            // all; otherwise it keeps its own style and takes the pasted runs.
            if (p0.table) first = { ...tableOf(p0), y: box.y };
            else first = head
                ? overlay({ ...box, text: head + p0.text, marks: shiftMarks(box.marks || [], start, text.length - start, 0) }, asObject(p0), head.length)
                : { ...box, ...p0.style, id: box.id, x: box.x, w: box.w, text: p0.text, marks: p0.marks };
            for (let i = 1; i < n - 1; i += 1) {
                const p = paragraphs[i];
                if (p.table) {
                    made.push({ ...tableOf(p), gapBefore: p.gapBefore || 8 });
                    continue;
                }
                made.push({ ...p.style, id: newId(p.style.type || OBJ.TEXT), x: p.x, w: p.w, y: 0, h: 0, text: p.text, marks: p.marks, gapBefore: p.gapBefore });
            }
            // Last: the pasted paragraph, then whatever followed the caret.
            let last = { ...pn.style, id: newId(pn.style.type || OBJ.TEXT), x: pn.x, w: pn.w, y: 0, h: 0, text: pn.text + tail, marks: pn.marks, gapBefore: pn.gapBefore };
            if (tail) {
                const after = { ...box, text: tail, marks: sliceMarks(box.marks, end, text.length) };
                last = overlay(last, after, pn.text.length);
            }
            made.push(last);
            caret = { id: last.id, offset: pn.text.length };
        }

        first = fit(first);
        // Stack the new paragraphs under the first, then push what followed it
        // on this page down by the same amount; pagination does the rest.
        let y = first.y + first.h;
        const placed = made.map(({ gapBefore, ...o }) => {
            const sized = fit({ ...o, y: y + (gapBefore || 0) });
            y = sized.y + sized.h;
            return sized;
        });
        const shift = y - (box.y + box.h);

        get().commit((st) => {
            const byUid = { ...st.objectsByUid };
            byUid[uid] = (st.objectsByUid[uid] || []).flatMap((o) => {
                if (o.id === box.id) return [first, ...placed];
                if (!o.role && o.y > box.y && shift > 0) return [{ ...o, y: o.y + shift }];
                return [o];
            });
            if (!st.documentMode) return { objectsByUid: byUid };
            const paged = paginateDocument(st.pages, byUid, byUid, st.docMargins);
            return {
                pages: paged.pages,
                objectsByUid: paged.objectsByUid,
                currentPage: Math.min(st.currentPage, paged.pages.length - 1),
            };
        });
        set({ docSelection: null, editingId: null, selectedId: caret.id });
        return caret;
    },

    replaceDocRange: (sel, insert = '') => {
        const state = get();
        const list = docFlowList(state);
        const ends = orderDocSelection(sel, list);
        if (!ends) return null;
        const head = list[ends.from.i];
        const tail = list[ends.to.i];
        const first = head.obj;
        const last = tail.obj;
        const firstText = String(first.text ?? '');
        const lastText = String(last.text ?? '');
        const s0 = ends.from.off;
        const e0 = ends.to.off;

        let next;
        if (first.id === last.id) {
            next = {
                ...first,
                text: firstText.slice(0, s0) + insert + firstText.slice(e0),
                marks: shiftMarks(first.marks || [], s0, e0 - s0, insert.length),
            };
        } else {
            const keep = firstText.slice(0, s0) + insert;
            const rest = lastText.slice(e0);
            let merged = {
                ...first,
                text: keep + rest,
                marks: shiftMarks(first.marks || [], s0, firstText.length - s0, insert.length),
            };
            // The tail's own runs (box style + marks), re-applied where it now sits.
            for (const run of runsOf(last)) {
                const from = Math.max(run.start, e0);
                const to = Math.min(run.start + run.text.length, lastText.length);
                if (to <= from) continue;
                const at = keep.length + (from - e0);
                merged = { ...merged, marks: markRange(merged, at, at + (to - from), runStyleOf(run.style)) };
            }
            next = merged;
        }
        next.h = Math.max(next.fontSize * (next.lineHeight || 1.35) + TEXT_PAD * 2, measuredTextHeight(next));

        const gone = new Set(list.slice(ends.from.i + 1, ends.to.i + 1).map((e) => e.obj.id));
        get().commit((st) => {
            const byUid = {};
            for (const [uid, objs] of Object.entries(st.objectsByUid)) {
                byUid[uid] = (objs || [])
                    .filter((o) => !gone.has(o.id))
                    .map((o) => (o.id === first.id ? next : o));
            }
            if (!st.documentMode) return { objectsByUid: byUid };
            const paged = paginateDocument(st.pages, st.objectsByUid, byUid, st.docMargins);
            return {
                pages: paged.pages,
                objectsByUid: paged.objectsByUid,
                currentPage: Math.min(st.currentPage, paged.pages.length - 1),
            };
        });
        set({ docSelection: null, selectedId: first.id });
        const after = get();
        const page = after.pages.find((pg) => (after.objectsByUid[pg.uid] || []).some((o) => o.id === first.id));
        return { uid: page?.uid, id: first.id, offset: s0 + insert.length };
    },

    setCurrentPage: (currentPage) => set({ currentPage }),
    /** Move to a page *and* scroll the viewport there (thumbnails, search, nav). */
    goToPage: (currentPage) => set((state) => ({ currentPage, scrollNonce: state.scrollNonce + 1 })),
    setViewMode: (viewMode) => set({ viewMode }),

    toggleThumbnails: () => set((state) => ({ showThumbnails: !state.showThumbnails })),
    toggleInspector: () => set((state) => ({ showInspector: !state.showInspector })),
    toggleRuler: () => set((state) => ({ showRuler: !state.showRuler })),
    toggleGrid: () => set((state) => ({ showGrid: !state.showGrid })),
    setDisplayMode: (displayMode) => set((state) => ({
        displayMode,
        // Web layout is one column at full width — the fit mode is part of what
        // the view means, not a separate thing to set afterwards.
        fitMode: displayMode === 'web' ? 'width' : state.fitMode,
        viewMode: displayMode === 'web' || displayMode === 'draft' ? 'continuous' : state.viewMode,
    })),
    toggleSplitView: () => set((state) => ({ splitView: !state.splitView })),
    setSplitRatio: (splitRatio) => set({ splitRatio: Math.min(0.85, Math.max(0.15, splitRatio)) }),
    setClipboard: (clipboard) => set({ clipboard }),

    toggleBookmarks: () => set((state) => ({ showBookmarks: !state.showBookmarks })),

    /* ----------------------------------------------------- track changes */

    toggleTrackChanges: () => set((state) => ({
        trackChanges: !state.trackChanges,
        // Turning it on is also a request to look at what happens next.
        showReviewing: !state.trackChanges ? true : state.showReviewing,
    })),
    toggleReviewing: () => set((state) => ({ showReviewing: !state.showReviewing })),

    /* ------------------------------------------------------ accessibility */

    toggleAccessibility: () => set((state) => ({ showAccessibility: !state.showAccessibility })),
    setAccessibilityIssues: (a11yIssues) => set({ a11yIssues, a11yChecked: true, showAccessibility: true }),

    /* -------------------------------------------------- margins and paper */

    setMargins: (margins, preset = null) => set({ margins, marginPreset: preset, showMargins: true }),
    toggleMargins: () => set((state) => ({ showMargins: !state.showMargins })),

    /**
     * Put the document on different paper.
     *
     * The page keeps its content: on export the original is embedded and drawn
     * to fit the new size, so an A4 contract printed on Letter is the same
     * contract, scaled — not a cropped one.
     */
    setPageSize: (size, { allPages = true, landscape = false } = {}) => get().commit((state) => {
        const width = landscape ? Math.max(size.width, size.height) : Math.min(size.width, size.height);
        const height = landscape ? Math.min(size.width, size.height) : Math.max(size.width, size.height);

        const pages = [];
        const objectsByUid = { ...state.objectsByUid };
        state.pages.forEach((page, index) => {
            if (!(allPages || index === state.currentPage)
                || (page.width === width && page.height === height)) {
                pages.push(page);
                return;
            }

            // Fit, never fill, and never stretch: the same transform the
            // exporter applies to the page's own content. Everything already on
            // the page moves with it, so a header stays centred on the header
            // and a patch stays over the paragraph it covers.
            const k = Math.min(width / page.width, height / page.height);
            const dx = (width - page.width * k) / 2;
            const dy = (height - page.height * k) / 2;

            pages.push({
                ...page,
                width,
                height,
                resized: true,
                // Cumulative, from the source page — which is the space pdf.js
                // reports text in, and the space the exporter scales from.
                contentScale: (page.contentScale || 1) * k,
                contentOffset: {
                    x: (page.contentOffset?.x || 0) * k + dx,
                    y: (page.contentOffset?.y || 0) * k + dy,
                },
            });

            objectsByUid[page.uid] = (state.objectsByUid[page.uid] || []).map((obj) => ({
                ...obj,
                x: obj.x * k + dx,
                y: obj.y * k + dy,
                w: obj.w * k,
                h: obj.h * k,
                ...(obj.fontSize ? { fontSize: obj.fontSize * k } : null),
                ...(obj.strokeWidth ? { strokeWidth: obj.strokeWidth * k } : null),
                // Freehand points are normalised inside the box, so scaling
                // the box carries the stroke with it.
                // `replaces` is a reference into the *source* page, which the
                // exporter reads before it rescales anything. Moving it would
                // point the strip at empty paper.
            }));
        });

        return { pages, objectsByUid };
    }),

    /**
     * Hyphenation, Word's document-wide switch, as a property every text box
     * carries — the wrapper is shared with the exporter and reads the box, so
     * the setting has to live where both of them already look.
     */
    setHyphenation: (on) => get().commit((state) => {
        const next = {};
        state.pages.forEach((page) => {
            next[page.uid] = (state.objectsByUid[page.uid] || []).map((obj) => (
                obj.type === 'text' ? { ...obj, hyphenate: on } : obj
            ));
        });
        return {
            objectsByUid: { ...state.objectsByUid, ...next },
            toolSettings: { ...state.toolSettings, text: { ...state.toolSettings.text, hyphenate: on } },
        };
    }),

    /**
     * Take one revision at its word (accept) or put it back (reject).
     *
     * A rejected addition goes away, a rejected edit gets its old properties
     * back, and a rejected deletion is simply un-marked — which is the whole of
     * what accept and reject mean once every edit carries its `before`.
     */
    resolveRevision: (id, accept) => get().commit((state) => {
        const revision = state.revisions.find((r) => r.id === id);
        if (!revision) return null;
        const list = state.objectsByUid[revision.uid] || [];
        let nextList = list;

        if (accept) {
            if (revision.kind === 'delete') {
                nextList = list.filter((o) => o.id !== revision.objectId && o.patchFor !== revision.objectId);
            } else {
                nextList = list.map((o) => (o.id === revision.objectId ? stripReviewMarks(o) : o));
            }
        } else if (revision.kind === 'add') {
            nextList = list.filter((o) => o.id !== revision.objectId && o.patchFor !== revision.objectId);
        } else if (revision.kind === 'delete') {
            nextList = list.map((o) => (o.id === revision.objectId ? stripReviewMarks(o) : o));
        } else {
            nextList = list.map((o) => (
                o.id === revision.objectId ? stripReviewMarks({ ...o, ...revision.before }) : o
            ));
        }

        let objectsByUid = { ...state.objectsByUid, [revision.uid]: nextList };
        if (!accept && revision.kind === 'edit') {
            objectsByUid = restoreAcrossPages(
                state,
                objectsByUid,
                revision,
                nextList.find((o) => o.id === revision.objectId),
            );
        }

        return {
            objectsByUid,
            revisions: state.revisions.filter((r) => r.id !== id),
            selectedId: state.selectedId === revision.objectId && (accept || revision.kind === 'add')
                ? null
                : state.selectedId,
        };
    }),

    resolveAllRevisions: (accept) => get().commit((state) => {
        let objectsByUid = state.objectsByUid;
        // Oldest first: a later edit to the same object has to land on top of
        // the earlier one it was made against.
        for (const revision of state.revisions) {
            const list = objectsByUid[revision.uid] || [];
            let nextList;
            if (accept) {
                nextList = revision.kind === 'delete'
                    ? list.filter((o) => o.id !== revision.objectId && o.patchFor !== revision.objectId)
                    : list.map((o) => (o.id === revision.objectId ? stripReviewMarks(o) : o));
            } else if (revision.kind === 'add') {
                nextList = list.filter((o) => o.id !== revision.objectId && o.patchFor !== revision.objectId);
            } else if (revision.kind === 'delete') {
                nextList = list.map((o) => (o.id === revision.objectId ? stripReviewMarks(o) : o));
            } else {
                nextList = list.map((o) => (
                    o.id === revision.objectId ? stripReviewMarks({ ...o, ...revision.before }) : o
                ));
            }
            objectsByUid = { ...objectsByUid, [revision.uid]: nextList };
            if (!accept && revision.kind === 'edit') {
                objectsByUid = restoreAcrossPages(
                    state,
                    objectsByUid,
                    revision,
                    nextList.find((o) => o.id === revision.objectId),
                );
            }
        }
        return { objectsByUid, revisions: [], selectedId: null };
    }),
    addBookmark: (title, pageIndex, level = 0) => get().commit((state) => {
        const entry = { id: newId('bm'), title, pageIndex, level };
        // Kept in page order, which is the order a reader expects an outline to
        // run in whatever order the entries were added.
        const next = [...state.bookmarks, entry]
            .sort((a, b) => a.pageIndex - b.pageIndex);
        return { bookmarks: next };
    }),
    updateBookmark: (id, patch) => get().commit((state) => ({
        bookmarks: state.bookmarks.map((b) => (b.id === id ? { ...b, ...patch } : b)),
    })),
    removeBookmark: (id) => get().commit((state) => ({
        bookmarks: state.bookmarks.filter((b) => b.id !== id),
    })),
    setStyleBrush: (styleBrush) => set({ styleBrush }),
    setSearchOpen: (showSearch) => set((state) => ({
        showSearch,
        search: showSearch ? state.search : { query: '', hits: [], index: 0, running: false },
    })),
    setPagesModal: (pagesModal) => set({ pagesModal }),
    setSignModal: (signModal) => set({ signModal }),
    setEmojiModal: (emojiModal) => set({ emojiModal }),
    setSymbolModal: (symbolModal) => set({ symbolModal }),
    setEquationModal: (equationModal) => set({ equationModal }),
    setLinkDraft: (linkDraft) => set({ linkDraft }),
    setSearch: (patch) => set((state) => ({ search: { ...state.search, ...patch } })),

    markSaved: () => set({ dirty: false }),
}));

/**
 * Locate the selected object and the page it belongs to.
 *
 * Deliberately NOT a zustand selector: it builds a fresh object, which would
 * fail the store's snapshot identity check and loop forever. Call it from a
 * `useMemo` over `selectedId` / `pages` / `objectsByUid` instead.
 */
export const findSelected = ({ selectedId, pages, objectsByUid }) => {
    if (!selectedId) return null;
    for (const page of pages) {
        const found = (objectsByUid[page.uid] || []).find((o) => o.id === selectedId);
        if (found) return { object: found, uid: page.uid };
    }
    return null;
};
